import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

function Agenda({ user }) {
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [currentView, setCurrentView] = useState('week');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draggedTask, setDraggedTask] = useState(null);
  const [isCreatingQuickTask, setIsCreatingQuickTask] = useState(false);
  const [quickTaskSlot, setQuickTaskSlot] = useState(null);

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    type: 'general',
    priority: 'medium',
    department: '',
    assigned_to: '',
    estimated_duration: 30,
    start_time: '',
    end_time: '',
    room_id: ''
  });

  useEffect(() => {
    loadAgendaData();
  }, [selectedDate, currentView]);

  // Gestion des raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (selectedTask && (event.key === 'Delete' || event.key === 'Backspace')) {
        event.preventDefault();
        handleDeleteTask(selectedTask.id);
      }
      if (selectedTask && event.key === 'Escape') {
        setSelectedTask(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedTask]);

  const loadAgendaData = async () => {
    try {
      setLoading(true);

      let startDate, endDate;
      if (currentView === 'day') {
        startDate = endDate = selectedDate.toISOString().split('T')[0];
      } else if (currentView === 'week') {
        const weekStart = getWeekStart(selectedDate);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        startDate = weekStart.toISOString().split('T')[0];
        endDate = weekEnd.toISOString().split('T')[0];
      } else if (currentView === 'month') {
        const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
        startDate = monthStart.toISOString().split('T')[0];
        endDate = monthEnd.toISOString().split('T')[0];
      }

      const [tasksRes, templatesRes, roomsRes] = await Promise.all([
        axios.get(`/api/tasks?start_date=${startDate}&end_date=${endDate}`),
        axios.get('/api/task-templates'),
        axios.get('/api/rooms')
      ]);

      setTasks(tasksRes.data);
      setTemplates(templatesRes.data);
      setUsers([{ id: user.id, username: user.username, full_name: user.full_name }]);
      setRooms(roomsRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données agenda:', error);
      setUsers([{ id: user.id, username: user.username, full_name: user.full_name }]);
    } finally {
      setLoading(false);
    }
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Lundi = début de semaine
    return new Date(d.setDate(diff));
  };

  const handleDateChange = (direction) => {
    const newDate = new Date(selectedDate);
    if (currentView === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (currentView === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (currentView === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  const formatDate = (date) => {
    if (currentView === 'day') {
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else if (currentView === 'week') {
      const weekStart = getWeekStart(date);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return `${weekStart.toLocaleDateString('fr-FR')} - ${weekEnd.toLocaleDateString('fr-FR')}`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long'
      });
    }
  };

  const formatTime = (dateTime) => {
    return new Date(dateTime).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ff4757';
      case 'medium': return '#ffa502';
      case 'low': return '#2ed573';
      default: return '#74b9ff';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'cleaning': return '#00b894';
      case 'maintenance': return '#e84393';
      case 'service': return '#0984e3';
      case 'security': return '#6c5ce7';
      case 'admin': return '#a29bfe';
      default: return '#74b9ff';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'cleaning': return '🧹';
      case 'maintenance': return '🔧';
      case 'service': return '🎯';
      case 'security': return '🛡️';
      case 'admin': return '📋';
      default: return '📝';
    }
  };

  const handleTaskDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleTaskDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTask(null);
  };

  const handleSlotDrop = async (e, newStartTime) => {
    e.preventDefault();

    if (!draggedTask) return;

    try {
      const oldStart = new Date(draggedTask.start_time);
      const newStart = new Date(newStartTime);
      const duration = new Date(draggedTask.end_time) - oldStart;
      const newEnd = new Date(newStart.getTime() + duration);

      await axios.put(`/api/tasks/${draggedTask.id}`, {
        ...draggedTask,
        start_time: newStart.toISOString(),
        end_time: newEnd.toISOString()
      });

      loadAgendaData();
    } catch (error) {
      console.error('Erreur lors du déplacement de la tâche:', error);
    }
  };

  const handleSlotDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleQuickCreate = async (startTime) => {
    const endTime = new Date(startTime.getTime() + 30 * 60000); // 30 minutes par défaut

    setNewTask({
      title: 'Nouvelle tâche',
      description: '',
      type: 'general',
      priority: 'medium',
      department: '',
      assigned_to: user.id,
      estimated_duration: 30,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      room_id: ''
    });

    setShowTaskModal(true);
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      if (!newTask.end_time && newTask.start_time) {
        const startDate = new Date(newTask.start_time);
        const endDate = new Date(startDate.getTime() + newTask.estimated_duration * 60000);
        newTask.end_time = endDate.toISOString();
      }

      await axios.post('/api/tasks', newTask);
      setShowTaskModal(false);
      resetNewTask();
      loadAgendaData();
    } catch (error) {
      console.error('Erreur lors de la création de la tâche:', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.post(`/api/tasks/${taskId}/complete`);
      loadAgendaData();
    } catch (error) {
      console.error('Erreur lors de la completion de la tâche:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible.')) {
      try {
        await axios.delete(`/api/tasks/${taskId}`);
        setSelectedTask(null);
        loadAgendaData();
      } catch (error) {
        console.error('Erreur lors de la suppression de la tâche:', error);
        alert('Erreur lors de la suppression de la tâche. Veuillez réessayer.');
      }
    }
  };

  const handleTaskContextMenu = (e, task) => {
    e.preventDefault();
    setSelectedTask(task);
  };

  const handleEditTask = (task) => {
    setNewTask(task);
    setSelectedTask(null);
    // S'assurer que la modal de détails est fermée avant d'ouvrir la modal d'édition
    setTimeout(() => setShowTaskModal(true), 50);
  };

  // Éviter l'ouverture simultanée des deux modals
  const openTaskModal = () => {
    setSelectedTask(null);
    setShowTaskModal(true);
  };

  const resetNewTask = () => {
    setNewTask({
      title: '',
      description: '',
      type: 'general',
      priority: 'medium',
      department: '',
      assigned_to: '',
      estimated_duration: 30,
      start_time: '',
      end_time: '',
      room_id: ''
    });
  };

  const renderHourGrid = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="hour-grid">
        {hours.map(hour => (
          <div key={hour} className="hour-row">
            <div className="hour-label-gcal">
              {hour === 0 ? '00:00' : `${hour.toString().padStart(2, '0')}:00`}
            </div>
            <div
              className="hour-slot-gcal"
              onDrop={(e) => {
                const date = new Date(selectedDate);
                date.setHours(hour, 0, 0, 0);
                handleSlotDrop(e, date);
              }}
              onDragOver={handleSlotDragOver}
              onClick={() => {
                const date = new Date(selectedDate);
                date.setHours(hour, 0, 0, 0);
                handleQuickCreate(date);
              }}
            >
              {renderTasksForHour(hour)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTasksForHour = (hour) => {
    const dayTasks = tasks.filter(task => {
      const taskDate = new Date(task.start_time);
      const taskHour = taskDate.getHours();
      const sameDay = taskDate.toDateString() === selectedDate.toDateString();

      return sameDay && taskHour === hour;
    });

    return dayTasks.map(task => (
      <div
        key={task.id}
        className={`task-event ${task.status}`}
        style={{
          backgroundColor: getTypeColor(task.type),
          borderLeft: `4px solid ${getPriorityColor(task.priority)}`
        }}
        draggable
        onDragStart={(e) => handleTaskDragStart(e, task)}
        onDragEnd={handleTaskDragEnd}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedTask(task);
        }}
        onContextMenu={(e) => {
          e.stopPropagation();
          handleTaskContextMenu(e, task);
        }}
      >
        <div className="task-event-content">
          <div className="task-event-title">
            <span className="task-event-icon">{getTypeIcon(task.type)}</span>
            {task.title}
          </div>
          <div className="task-event-time">
            {formatTime(task.start_time)} - {formatTime(task.end_time)}
          </div>
          {task.assigned_full_name && (
            <div className="task-event-assignee">👤 {task.assigned_full_name}</div>
          )}
        </div>
      </div>
    ));
  };

  const renderWeekView = () => {
    const weekStart = getWeekStart(selectedDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      return day;
    });

    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="week-view">
        <div className="week-header">
          <div className="week-time-column"></div>
          {weekDays.map(day => (
            <div key={day.toDateString()} className="week-day-header">
              <div className="week-day-name">{day.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
              <div className="week-day-number">{day.getDate()}</div>
            </div>
          ))}
        </div>

        <div className="week-content">
          <div className="week-hours">
            {hours.map(hour => (
              <div key={hour} className="week-hour-label">
                {hour === 0 ? '00:00' : `${hour.toString().padStart(2, '0')}:00`}
              </div>
            ))}
          </div>

          <div className="week-grid">
            {weekDays.map(day => (
              <div key={day.toDateString()} className="week-day-column">
                {hours.map(hour => (
                  <div
                    key={hour}
                    className="week-hour-slot"
                    onDrop={(e) => {
                      const slotDate = new Date(day);
                      slotDate.setHours(hour, 0, 0, 0);
                      handleSlotDrop(e, slotDate);
                    }}
                    onDragOver={handleSlotDragOver}
                    onClick={() => {
                      const slotDate = new Date(day);
                      slotDate.setHours(hour, 0, 0, 0);
                      handleQuickCreate(slotDate);
                    }}
                  >
                    {renderTasksForWeekSlot(day, hour)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTasksForWeekSlot = (day, hour) => {
    const slotTasks = tasks.filter(task => {
      const taskDate = new Date(task.start_time);
      const taskHour = taskDate.getHours();
      const sameDay = taskDate.toDateString() === day.toDateString();

      return sameDay && taskHour === hour;
    });

    return slotTasks.map(task => (
      <div
        key={task.id}
        className={`week-task-event ${task.status}`}
        style={{
          backgroundColor: getTypeColor(task.type),
          borderLeft: `3px solid ${getPriorityColor(task.priority)}`
        }}
        draggable
        onDragStart={(e) => handleTaskDragStart(e, task)}
        onDragEnd={handleTaskDragEnd}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedTask(task);
        }}
        onContextMenu={(e) => {
          e.stopPropagation();
          handleTaskContextMenu(e, task);
        }}
      >
        <div className="week-task-title">
          {getTypeIcon(task.type)} {task.title}
        </div>
      </div>
    ));
  };

  const renderMonthView = () => {
    const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

    // Commencer par le lundi de la première semaine
    const calendarStart = getWeekStart(monthStart);
    const calendarEnd = new Date(monthEnd);
    calendarEnd.setDate(calendarEnd.getDate() + (6 - calendarEnd.getDay()));

    const calendarDays = [];
    const current = new Date(calendarStart);

    while (current <= calendarEnd) {
      calendarDays.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    return (
      <div className="month-view">
        <div className="month-header">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
            <div key={day} className="month-day-header">{day}</div>
          ))}
        </div>

        <div className="month-grid">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="month-week">
              {week.map(day => (
                <div
                  key={day.toDateString()}
                  className={`month-day ${
                    day.getMonth() !== selectedDate.getMonth() ? 'other-month' : ''
                  } ${day.toDateString() === new Date().toDateString() ? 'today' : ''}`}
                  onClick={() => {
                    setSelectedDate(new Date(day));
                    setCurrentView('day');
                  }}
                >
                  <div className="month-day-number">{day.getDate()}</div>
                  <div className="month-day-tasks">
                    {renderMonthDayTasks(day)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMonthDayTasks = (day) => {
    const dayTasks = tasks.filter(task => {
      const taskDate = new Date(task.start_time);
      return taskDate.toDateString() === day.toDateString();
    });

    return dayTasks.slice(0, 3).map(task => (
      <div
        key={task.id}
        className="month-task-event"
        style={{ backgroundColor: getTypeColor(task.type) }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedTask(task);
        }}
      >
        <span className="month-task-icon">{getTypeIcon(task.type)}</span>
        <span className="month-task-title">{task.title}</span>
      </div>
    ));
  };

  const renderTaskModal = () => {
    if (!showTaskModal) return null;

    return (
      <div className="task-modal-overlay">
        <div className="task-modal">
          <div className="modal-header">
            <h3>📝 {selectedTask ? 'Modifier la tâche' : 'Nouvelle Tâche'}</h3>
            <button
              className="close-btn"
              onClick={() => {
                setShowTaskModal(false);
                setSelectedTask(null);
                resetNewTask();
              }}
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleCreateTask} className="task-form">
            <div className="form-group">
              <label>Titre *</label>
              <input
                type="text"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                required
                autoFocus
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <select
                  value={newTask.type}
                  onChange={(e) => setNewTask({ ...newTask, type: e.target.value })}
                >
                  <option value="general">Général</option>
                  <option value="cleaning">Nettoyage</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="service">Service</option>
                  <option value="security">Sécurité</option>
                  <option value="admin">Administration</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priorité</label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                >
                  <option value="low">Basse</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Haute</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Département</label>
                <select
                  value={newTask.department}
                  onChange={(e) => setNewTask({ ...newTask, department: e.target.value })}
                >
                  <option value="">Sélectionner...</option>
                  <option value="reception">Réception</option>
                  <option value="housekeeping">Ménage</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="security">Sécurité</option>
                  <option value="restaurant">Restaurant</option>
                </select>
              </div>

              <div className="form-group">
                <label>Assigné à</label>
                <select
                  value={newTask.assigned_to}
                  onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })}
                >
                  <option value="">Non assigné</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.username}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Heure de début *</label>
                <input
                  type="datetime-local"
                  value={newTask.start_time ? newTask.start_time.slice(0, 16) : ''}
                  onChange={(e) => setNewTask({ ...newTask, start_time: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Durée (minutes)</label>
                <input
                  type="number"
                  value={newTask.estimated_duration}
                  onChange={(e) => setNewTask({ ...newTask, estimated_duration: parseInt(e.target.value) })}
                  min="5"
                  step="5"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Chambre (optionnel)</label>
              <select
                value={newTask.room_id}
                onChange={(e) => setNewTask({ ...newTask, room_id: e.target.value })}
              >
                <option value="">Aucune chambre</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>
                    {room.room_number} - {room.type}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                rows="3"
              />
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowTaskModal(false);
                  setSelectedTask(null);
                  resetNewTask();
                }}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button type="submit" className="btn btn-primary">
                {selectedTask ? 'Modifier' : 'Créer'} la tâche
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="agenda-container">
        <div className="loading-stats">
          <div className="loading-spinner"></div>
          <p>Chargement de l'agenda...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agenda-container">
      <div className="agenda-header">
        <div className="agenda-title-section">
          <h2 className="agenda-title">
            <span className="title-icon">📅</span>
            Agenda & Planification
          </h2>
          <div className="agenda-subtitle">
            Gestion des tâches et activités de l'hôtel
          </div>
        </div>

        <div className="agenda-controls">
          <div className="view-selector">
            <button
              className={`view-btn ${currentView === 'day' ? 'active' : ''}`}
              onClick={() => setCurrentView('day')}
            >
              Jour
            </button>
            <button
              className={`view-btn ${currentView === 'week' ? 'active' : ''}`}
              onClick={() => setCurrentView('week')}
            >
              Semaine
            </button>
            <button
              className={`view-btn ${currentView === 'month' ? 'active' : ''}`}
              onClick={() => setCurrentView('month')}
            >
              Mois
            </button>
          </div>

          <div className="date-navigation">
            <button className="nav-btn" onClick={() => handleDateChange(-1)}>
              ←
            </button>
            <span className="current-date">{formatDate(selectedDate)}</span>
            <button className="nav-btn" onClick={() => handleDateChange(1)}>
              →
            </button>
            <button
              className="today-btn"
              onClick={() => setSelectedDate(new Date())}
            >
              Aujourd'hui
            </button>
          </div>

          <button
            className="btn btn-primary create-task-btn"
            onClick={openTaskModal}
          >
            + Nouvelle Tâche
          </button>
        </div>
      </div>

      <div className="agenda-content">
        {currentView === 'day' && renderHourGrid()}
        {currentView === 'week' && renderWeekView()}
        {currentView === 'month' && renderMonthView()}
      </div>

      {renderTaskModal()}

      {selectedTask && (
        <div className="task-details-modal">
          <div className="task-details-content">
            <div className="task-details-header">
              <h3>{selectedTask.title}</h3>
              <div className="task-details-header-actions">
                <small className="keyboard-hint">Appuyez sur Échap pour fermer, Suppr pour effacer</small>
                <button onClick={() => setSelectedTask(null)}>✕</button>
              </div>
            </div>
            <div className="task-details-body">
              <div className="task-detail-item">
                <strong>Type:</strong> {getTypeIcon(selectedTask.type)} {selectedTask.type}
              </div>
              <div className="task-detail-item">
                <strong>Priorité:</strong>
                <span className={`priority-badge ${selectedTask.priority}`}>
                  {selectedTask.priority}
                </span>
              </div>
              <div className="task-detail-item">
                <strong>Statut:</strong>
                <span className={`status-badge ${selectedTask.status}`}>
                  {selectedTask.status}
                </span>
              </div>
              <div className="task-detail-item">
                <strong>Horaire:</strong> {formatTime(selectedTask.start_time)} - {formatTime(selectedTask.end_time)}
              </div>
              {selectedTask.assigned_full_name && (
                <div className="task-detail-item">
                  <strong>Assigné à:</strong> {selectedTask.assigned_full_name}
                </div>
              )}
              {selectedTask.room_number && (
                <div className="task-detail-item">
                  <strong>Chambre:</strong> {selectedTask.room_number}
                </div>
              )}
              {selectedTask.description && (
                <div className="task-detail-item">
                  <strong>Description:</strong> {selectedTask.description}
                </div>
              )}

              <div className="task-detail-actions">
                {selectedTask.status !== 'completed' && (
                  <button
                    className="btn btn-success btn-small"
                    onClick={() => {
                      handleCompleteTask(selectedTask.id);
                      setSelectedTask(null);
                    }}
                  >
                    ✓ Marquer terminé
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => handleEditTask(selectedTask)}
                >
                  ✏️ Modifier
                </button>
                <button
                  className="btn btn-danger btn-small"
                  onClick={() => handleDeleteTask(selectedTask.id)}
                >
                  🗑️ Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Agenda;